/* eslint-disable react/no-unescaped-entities */
import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { postOrQueue } from "../lib/offline";
import { speak } from "../lib/i18n";
import { useApp } from "../contexts/AppContext";
import { toast } from "sonner";
import { Mic, Square, Play, Pause, Loader2, Volume2, Check, CheckCheck } from "lucide-react";

const MAX_S = 60;

function pickMime() {
  const c = ["audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus", "audio/mp4"];
  return c.find((m) => window.MediaRecorder && MediaRecorder.isTypeSupported(m)) || "";
}

function blobToB64(blob) {
  return new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(blob); });
}

/** Bouton micro : appuyer pour parler, appuyer à nouveau (ou relâcher) pour envoyer. */
export function VoiceRecorder({ threadId, destinataireId, onSent, size = "lg" }) {
  const [state, setState] = useState("idle"); // idle | rec | sending
  const [secs, setSecs] = useState(0);
  const rec = useRef(null);
  const chunks = useRef([]);
  const timer = useRef(null);
  const startedAt = useRef(0);
  const supported = typeof window !== "undefined" && !!navigator.mediaDevices?.getUserMedia && !!window.MediaRecorder;

  useEffect(() => () => { clearInterval(timer.current); rec.current?.stream?.getTracks().forEach((t) => t.stop()); }, []);

  const start = async () => {
    if (!threadId || !destinataireId) { toast.error("Aucun praticien rattaché"); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = pickMime();
      const r = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunks.current = [];
      r.ondataavailable = (e) => { if (e.data.size) chunks.current.push(e.data); };
      r.onstop = () => send(r.mimeType || mime || "audio/webm");
      r.start(250);
      rec.current = r;
      startedAt.current = Date.now();
      setSecs(0); setState("rec");
      timer.current = setInterval(() => {
        const s = Math.round((Date.now() - startedAt.current) / 1000);
        setSecs(s);
        if (s >= MAX_S) stop();
      }, 500);
    } catch (e) {
      toast.error("Micro indisponible. Autorisez l'accès au micro dans le navigateur.");
    }
  };

  const stop = () => {
    clearInterval(timer.current);
    const r = rec.current;
    if (r && r.state !== "inactive") { r.stop(); r.stream.getTracks().forEach((t) => t.stop()); }
  };

  const send = async (mime) => {
    const blob = new Blob(chunks.current, { type: mime });
    const duree = Math.max(1, Math.round((Date.now() - startedAt.current) / 1000));
    if (blob.size < 500 || duree < 1) { setState("idle"); return; }
    setState("sending");
    const audio_b64 = await blobToB64(blob);
    const res = await postOrQueue("/messages/voice", { thread_id: threadId, destinataire_id: destinataireId, audio_b64, mime, duree_s: duree, client_date: new Date().toISOString() });
    if (res._queued) toast.info("Message vocal enregistré hors-ligne. Sera envoyé au retour du réseau.");
    else toast.success("Message vocal envoyé");
    setState("idle");
    onSent?.(res, duree);
  };

  const onPointerDown = () => { if (state === "idle") start(); };
  const onPointerUp = () => { if (state === "rec" && Date.now() - startedAt.current > 700) stop(); };
  const onClick = () => { if (state === "rec" && Date.now() - startedAt.current <= 700) { /* tap: reste en enregistrement */ } };

  if (!supported) return null;
  const big = size === "lg";
  return (
    <div className="flex items-center gap-2">
      <button
        data-testid="voice-record-btn"
        aria-label={state === "rec" ? "Arrêter et envoyer" : "Appuyer pour parler"}
        onPointerDown={onPointerDown} onPointerUp={onPointerUp} onClick={state === "rec" ? () => stop() : onClick}
        disabled={state === "sending"}
        className={`${big ? "w-14 h-14" : "w-11 h-11"} rounded-full grid place-items-center text-white select-none transition-colors ${state === "rec" ? "bg-[#D9534F] animate-pulse" : "bg-[#0A1426]"} disabled:opacity-60`}
      >
        {state === "sending" ? <Loader2 className="animate-spin" size={big ? 22 : 18} /> : state === "rec" ? <Square size={big ? 20 : 16} /> : <Mic size={big ? 24 : 18} />}
      </button>
      {state === "rec" && <span data-testid="voice-rec-timer" className="text-[13px] font-bold text-[#D9534F] tabular-nums">{secs}s · appuyez pour envoyer</span>}
    </div>
  );
}

/** Lecteur d'un message vocal (téléchargement authentifié en blob à la demande) + transcription Whisper. */
export function VoiceBubble({ msg, mine, onRefresh }) {
  const { lang } = useApp();
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const audio = useRef(null);
  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);
  const pending = msg.transcription_statut === "en_cours";
  useEffect(() => {
    if (!pending || !onRefresh) return;
    const t = setTimeout(onRefresh, 4000);
    return () => clearTimeout(t);
  }, [pending, msg.id]); // eslint-disable-line

  const retry = async (e) => {
    e.stopPropagation();
    try { await api.post(`/messages/${msg.id}/transcribe`); toast.info("Transcription relancée"); setTimeout(() => onRefresh?.(), 5000); }
    catch { toast.error("Relance impossible"); }
  };

  const toggle = async () => {
    if (playing) { audio.current?.pause(); setPlaying(false); return; }
    let src = url;
    if (!src) {
      if (!msg.audio_id) { toast.info("En attente d'envoi…"); return; }
      setLoading(true);
      try {
        const { data } = await api.get(`/messages/voice/${msg.audio_id}`, { responseType: "blob" });
        src = URL.createObjectURL(data); setUrl(src);
      } catch { toast.error("Audio indisponible"); setLoading(false); return; }
      setLoading(false);
    }
    const a = new Audio(src);
    audio.current = a;
    a.onended = () => setPlaying(false);
    a.play().then(() => setPlaying(true)).catch(() => toast.error("Lecture impossible"));
    if (!mine && msg.id && !String(msg.id).startsWith("tmp-") && !msg.ecoute_le) {
      api.post(`/messages/${msg.id}/listened`).then(() => onRefresh?.()).catch(() => null);
    }
  };

  const receipt = mine && msg.ecoute_le
    ? `Écouté par ${msg.ecoute_role === "therapeute" ? "votre praticien" : msg.ecoute_role === "aidant" ? "l'aidant" : "le patient"} le ${new Date(msg.ecoute_le).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}`
    : mine && !String(msg.id).startsWith("tmp-") ? "Pas encore écouté" : null;

  return (
    <div className={`flex flex-col gap-1 max-w-[85%] ${mine ? "items-end" : "items-start"}`}>
      <button data-testid={`voice-msg-${mine ? "mine" : "them"}`} onClick={toggle}
        className={`flex items-center gap-2 px-3 py-2 rounded-2xl text-[13px] font-bold whitespace-nowrap ${mine ? "bg-[#2BA3A0] text-white rounded-br-md" : "bg-[#F5F3EE] text-[#0A1426] rounded-bl-md"}`}>
        <span className={`w-8 h-8 rounded-full grid place-items-center ${mine ? "bg-white/20" : "bg-white"}`}>
          {loading ? <Loader2 size={14} className="animate-spin" /> : playing ? <Pause size={14} /> : <Play size={14} />}
        </span>
        <span>Message vocal · {msg.duree_s ?? "?"} s</span>
        {msg.statut === "file" && <span className="text-[10px] opacity-80">En file…</span>}
      </button>
      {msg.transcription && (
        <div className={`flex items-start gap-1.5 max-w-full ${mine ? "flex-row-reverse" : ""}`}>
          <p data-testid="voice-transcription" className={`text-[12px] leading-snug px-3 py-1.5 rounded-xl italic ${mine ? "bg-[#2BA3A0]/10 text-[#0A1426]" : "bg-white border border-[#E4E0D5] text-[#0A1426]"}`}>
            « {msg.transcription} »
          </p>
          <button data-testid="voice-read-text-btn" aria-label="Lire le texte à voix haute" onClick={(e) => { e.stopPropagation(); speak(msg.transcription, lang); }}
            className="w-8 h-8 shrink-0 rounded-full bg-[#0A1426] text-white grid place-items-center"><Volume2 size={14} /></button>
        </div>
      )}
      {pending && <span data-testid="voice-transcription-pending" className="text-[10px] text-[#0A1426]/60 flex items-center gap-1 px-1"><Loader2 size={10} className="animate-spin" /> Transcription en cours…</span>}
      {receipt && <span data-testid={msg.ecoute_le ? "voice-receipt-listened" : "voice-receipt-pending"} className={`text-[10px] px-1 flex items-center gap-1 ${msg.ecoute_le ? "text-[#2BA3A0] font-bold" : "text-[#0A1426]/50"}`}>{msg.ecoute_le ? <CheckCheck size={12} /> : <Check size={12} />} {receipt}</span>}
      {(msg.transcription_statut === "echec" || msg.transcription_statut === "vide") && (
        <button data-testid="voice-transcription-retry" onClick={retry} className="text-[10px] text-[#8A6D2B] font-bold px-1 underline">
          {msg.transcription_statut === "vide" ? "Aucune parole détectée · réessayer" : "Transcription indisponible · réessayer"}
        </button>
      )}
    </div>
  );
}
